import { Label } from '@ydistri/ds';
import TextArea from 'antd/es/input/TextArea';
import React, {
  ChangeEvent,
  ClipboardEvent,
  ClipboardEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { insertText } from '@ydistri/generic-utils';
import { InputRef } from 'antd';
import {
  ImportableItem,
  ImportedItemParser,
  ImportedItemRow,
  ImportedItemValidator,
  ImportFormatItem,
} from '../../../../../../../lib/imports/importTypes';

const isWholeTextSelected = (selectionStart: number, selectionEnd: number, text: string) => {
  if (text.length > 0) {
    return selectionEnd - selectionStart === text.length;
  }
  return false;
};

interface ListItemsTextAreaProps<T extends ImportableItem> {
  id?: string;
  label?: string;
  placeholder?: string;
  value?: string;
  parserProvider: (dataTypes: readonly string[]) => ImportedItemParser<T> | null;
  onValueChanged: (newValue: string, rows?: readonly ImportedItemRow<T>[]) => void;
  serializer: (data: readonly ImportedItemRow<T>[]) => string;
  inputFormat: readonly ImportFormatItem<T>[];
  validators: ImportedItemValidator<T>[];
  disabled?: boolean;
}

/**
 * Text area for pasting items in a list format.
 * This component should not depend on the state, so it can be used in different contexts.
 * @param label
 * @param id
 * @param placeholder
 * @param parserProvider
 * @param value
 * @param onValueChanged
 * @param serializer
 * @param inputFormat
 * @param validators
 * @param disabled
 * @constructor
 */
const ListItemsTextArea = <T extends ImportableItem>({
  label,
  id,
  placeholder,
  parserProvider,
  value,
  onValueChanged,
  serializer,
  inputFormat,
  validators,
  disabled = false,
}: ListItemsTextAreaProps<T>): React.ReactElement => {
  const ref = useRef<InputRef>(null);
  const [selectionStart, setSelectionStart] = useState(0);
  const [selectionEnd, setSelectionEnd] = useState(0);

  // we need to restore selection (cursor position) after the value change
  useEffect(() => {
    if (document.activeElement === ref.current?.input) {
      ref.current.setSelectionRange(selectionStart, selectionEnd);
    }
  }, [selectionEnd, selectionStart, value]); // run after every value change

  const onTextSelect = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const start = event.target.selectionStart;
    const end = event.target.selectionEnd;

    setSelectionStart(start);
    setSelectionEnd(end);
  }, []);

  const pasteHandler: ClipboardEventHandler<HTMLTextAreaElement | HTMLDivElement> = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement | HTMLDivElement>) => {
      const clipboardData = event.clipboardData;
      const parser = parserProvider(clipboardData.types);
      if (parser !== null) {
        const pastedData = clipboardData.getData(parser.contentType);
        const pastedRows = parser.parse(pastedData, inputFormat, validators);
        const formattedPastedRows = serializer(pastedRows);

        let newRows: typeof pastedRows;
        let newFormattedContent: string;

        if (value) {
          // Field is not empty: insert a formatted fragment, then parse and serialize everything
          let newContent: string;
          if (isWholeTextSelected(selectionStart, selectionEnd, value)) {
            newContent = formattedPastedRows;
          } else {
            newContent = insertText(value, formattedPastedRows, selectionStart, selectionEnd);
          }
          const textParser = parserProvider(['text/plain']);
          if (textParser === null) {
            throw new Error('Text parser is not available');
          }
          newRows = textParser.parse(newContent, inputFormat, validators);
          newFormattedContent = serializer(newRows);
        } else {
          // Field was empty: we already have everything parsed and serialized
          newRows = pastedRows;
          newFormattedContent = formattedPastedRows;
        }

        onValueChanged(newFormattedContent, newRows);
        event.preventDefault();
      }
    },
    [
      parserProvider,
      inputFormat,
      validators,
      serializer,
      value,
      onValueChanged,
      selectionStart,
      selectionEnd,
    ],
  );

  const processContentChanged = useCallback(
    (content: string) => {
      const parser = parserProvider(['text/plain']);
      if (parser !== null) {
        const rows = parser.parse(content, inputFormat, validators);
        onValueChanged(content, rows);
      } else {
        onValueChanged(content);
      }
    },
    [inputFormat, onValueChanged, parserProvider, validators],
  );

  const changeHandler = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setSelectionStart(e.target.selectionStart);
      setSelectionEnd(e.target.selectionEnd);
      processContentChanged(e.target.value);
    },
    [processContentChanged],
  );

  return (
    <>
      {label && <Label>{label}</Label>}
      <TextArea
        ref={ref}
        rows={22}
        value={value}
        placeholder={placeholder}
        id={id ?? 'ListItemsInput'}
        onChange={changeHandler}
        onPaste={pasteHandler}
        onSelect={onTextSelect}
        disabled={disabled}
      />
    </>
  );
};

export default ListItemsTextArea;
